/**
 * @file Region UI Manager
 * @description UI updates and manipulation for region component
 */

import { SELECTORS } from "./constants.js";
import {
  getElement,
  updateElementContent,
  addClass,
  removeClass,
  createElement,
} from "./dom-manager.js";
import { showElement, hideElement } from "./utils.js";

/**
 * Update province listing with API data
 * @param {Array} provinces - Array of province data
 */
export function updateProvinceListing(provinces) {
  const provinceListing = getElement("provinceListing");
  if (!provinceListing || !provinces) return;

  provinceListing.innerHTML = '<option value="">Select province --</option>';

  provinces.forEach((item) => {
    const option = createElement("option", {
      "data-name": item.id,
      value: item.link,
      textContent: `${item.name} (${item.industry_post_count})`,
    });
    provinceListing.appendChild(option);
  });

  // Remove loading classes
  removeClass(provinceListing, "lg:hidden");
  removeClass(provinceListing, "lg:opacity-30");
  removeClass(provinceListing, "pointer-events-none");
}

/**
 * Update region description
 * @param {Object} regionData - Region data
 */
export function updateRegionDescription(regionData) {
  const regionDescription = getElement("regionDescription");
  const mainDescription = getElement("mainDescription");

  if (!regionDescription || !regionData) return;

  updateElementContent(regionDescription, regionData.description);

  // Hide main description and show region description
  if (mainDescription) {
    hideElement(mainDescription);
    showElement(regionDescription);
    removeClass(regionDescription, "opacity-30");
  }
}

/**
 * Show child map for a region with callback pattern like original code
 * @param {string} regionSlug - Region slug
 */
export async function showChildMap(regionSlug) {
  const { findChildMap } = await import("./utils.js");
  const childMap = findChildMap(regionSlug, SELECTORS.CHILD_MAPS);

  if (childMap) {
    // Use callback pattern like original code to ensure proper timing
    return new Promise(async (resolve) => {
      // First hide all child maps
      await hideAllChildMaps();

      // Then show the specific child map with callback
      await showElement(childMap);

      // Show view-region-ips button
      const viewRegionIps = childMap.querySelector(SELECTORS.VIEW_REGION_IPS);
      if (viewRegionIps) {
        viewRegionIps.style.display = "flex";
      }

      resolve();
    });
  }
}

/**
 * Hide all child maps
 */
export async function hideAllChildMaps() {
  const { getElementsBySelector } = await import("./dom-manager.js");
  const allChildMaps = getElementsBySelector(SELECTORS.CHILD_MAPS);

  // Convert NodeList to Array and hide all child maps simultaneously to avoid flickering
  const hidePromises = Array.from(allChildMaps).map((map) => hideElement(map));
  await Promise.all(hidePromises);
}

/**
 * Show regions with child maps and data updates
 * @param {string} regionDataID - Region data ID (term ID) for API calls
 * @param {string} regionSlug - Region slug for child map matching
 */
export async function showRegions(regionDataID, regionSlug) {
  const { fetchProvinces, fetchRegionDetails } = await import("./api-service.js");
  const { toggleLoading } = await import("./utils.js");
  const loadingIndicator = getElement("loadingIndicator");
  const vietnamMap = getElement("vietnamMap");

  try {
    toggleLoading(loadingIndicator, true);

    const [provinces, regionData] = await Promise.all([
      fetchProvinces(regionDataID),
      fetchRegionDetails(regionDataID),
    ]);

    // Update province listing
    updateProvinceListing(provinces);

    // Update region description
    updateRegionDescription(regionData);

    // Hide VNMAP and show child map with proper callback timing
    if (vietnamMap) {
      // Use callback pattern like original code to avoid flickering
      await hideElement(vietnamMap);
      // hideAllChildMaps() is now called inside showChildMap() to avoid double calls
      await showChildMap(regionSlug);
    }

    toggleLoading(loadingIndicator, false);
  } catch (error) {
    console.error("Error in showRegions:", error);
    toggleLoading(loadingIndicator, false);
  }
}

/**
 * Reset region display to initial state
 */
export async function resetRegionDisplay() {
  const mainDescription = getElement("mainDescription");
  const regionDescription = getElement("regionDescription");

  try {
    await hideAllChildMaps();

    // Reset region items
    const { getElementsBySelector, removeClass } = await import("./dom-manager.js");
    const regionItems = getElementsBySelector(SELECTORS.REGION_ITEMS);
    Array.from(regionItems).forEach((item) => removeClass(item, "active"));

    // Show main description
    if (mainDescription) {
      await showElement(mainDescription);
    }

    // Hide region description
    if (regionDescription) {
      await hideElement(regionDescription);
    }
  } catch (error) {
    console.error("Error resetting region display:", error);
  }
}

/**
 * Set loading state for province listing
 */
export function setProvinceListingLoading() {
  const provinceListing = getElement("provinceListing");
  if (provinceListing) {
    updateElementContent(provinceListing, "<option>Loading...</option>");
    addClass(provinceListing, "lg:opacity-30");
    addClass(provinceListing, "pointer-events-none");
  }
}

/**
 * Set loading state for region description
 */
export function setRegionDescriptionLoading() {
  const regionDescription = getElement("regionDescription");
  if (regionDescription) {
    addClass(regionDescription, "opacity-30");
  }
}

/**
 * Update region select options
 * @param {Array} regions - Array of region data
 */
export function updateRegionSelect(regions) {
  const regionSelect = getElement("regionSelect");
  if (!regionSelect || !regions) return;

  regionSelect.innerHTML = '<option value="">Select region --</option>';

  regions.forEach((region) => {
    const option = createElement("option", {
      value: region.id,
      "data-region-id": region.id,
      textContent: region.name,
    });
    regionSelect.appendChild(option);
  });
}

/**
 * Show/hide loading indicator
 * @param {boolean} isLoading - Whether to show loading
 */
export async function showLoading(isLoading) {
  const { toggleLoading } = await import("./utils.js");
  const loadingIndicator = getElement("loadingIndicator");
  toggleLoading(loadingIndicator, isLoading);
}

/**
 * Handle back button functionality
 */
export async function handleBackButton() {
  const { getElement } = await import("./dom-manager.js");
  const osmWrapper = getElement("osmWrapper");
  const { getElementsBySelector } = await import("./dom-manager.js");
  const { findCorrespondingNavLink } = await import("./utils.js");

  const activeItem = getElementsBySelector(SELECTORS.REGION_ITEM_ACTIVE)[0];
  const regionName = activeItem?.getAttribute("data-region");

  if (osmWrapper) {
    await hideElement(osmWrapper);
    const childMap = getElementsBySelector(SELECTORS.CHILD_MAPS).find(
      (map) => map.getAttribute("data-map") === regionName,
    );
    if (childMap) {
      await showElement(childMap);
    }
  }
}
